import { inject, injectable, multiInject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core';
import { OarTaskContentModel } from '../../models/oar-task-content-model';
import { OarTaskDataLoader } from '../../loaders/oar-task-data-loader';
import type { OarTaskDataOrchestrator, LoaderProgressEvent } from '../oar-task-data-orchestrator';

/**
 * OAR 任务数据编排服务的默认实现
 *
 * 符合开闭原则：
 * - 新增数据源只需实现 OarTaskDataLoader 接口并注册到 DI 容器
 * - 不需要修改此类的任何代码
 *
 * 执行策略：
 * 1. 第一阶段：执行 TaskInfo Loader（获取解析状态）
 * 2. 第二阶段：根据解析状态，串行执行其他 Loader
 */
@injectable()
export class DefaultOarTaskDataOrchestrator implements OarTaskDataOrchestrator {
    private readonly _loaders: Map<string, OarTaskDataLoader>;
    private readonly _onLoaderProgressEmitter = new Emitter<LoaderProgressEvent>();

    /**
     * Loader 加载进度事件
     */
    readonly onLoaderProgress = this._onLoaderProgressEmitter.event;

    constructor(
        @inject(OarTaskContentModel)
        private readonly _model: OarTaskContentModel,

        // 自动注入所有注册的 Loader
        @multiInject(OarTaskDataLoader)
        loaders: OarTaskDataLoader[],
    ) {
        // 将 Loader 按名称索引，便于查找
        this._loaders = new Map(loaders.map(loader => [loader.name, loader]));
    }

    /**
     * 加载所有可用的数据
     */
    async loadAll(taskId: string): Promise<void> {
        // 第一阶段：加载任务基本信息
        const taskInfoLoader = this._loaders.get('TaskInfo');
        if (taskInfoLoader) {
            await this._executeLoader(taskId, taskInfoLoader);
        }

        // 第二阶段：串行加载其他数据
        const currentState = this._model.modelData;
        const otherLoaders = Array.from(this._loaders.values())
            .filter(loader => loader.name !== 'TaskInfo')
            .filter(loader => loader.shouldLoad(currentState));

        // 串行执行每个 Loader
        for (const loader of otherLoaders) {
            try {
                await this._executeLoader(taskId, loader);
            } catch (error) {
                // 继续执行下一个 Loader，不中断流程
            }
        }
    }

    /**
     * 加载特定部分的数据
     */
    async loadPart(taskId: string, loaderName: string): Promise<void> {
        const loader = this._loaders.get(loaderName);
        if (!loader) {
            throw new Error(`Loader '${loaderName}' not found. Available loaders: ${Array.from(this._loaders.keys()).join(', ')}`);
        }

        await this._executeLoader(taskId, loader);
    }

    /**
     * 执行单个 Loader
     */
    private async _executeLoader(taskId: string, loader: OarTaskDataLoader): Promise<void> {
        const currentState = this._model.modelData;

        // 检查是否应该执行
        if (!loader.shouldLoad(currentState)) {
            return;
        }

        try {
            // 发射开始事件
            this._onLoaderProgressEmitter.fire({
                loaderName: loader.name,
                status: 'start',
            });

            // 加载数据
            const updates = await loader.load(taskId);

            // 更新到 Model
            this._model.update(state => {
                return { ...state, ...updates };
            });

            // 发射完成事件
            this._onLoaderProgressEmitter.fire({
                loaderName: loader.name,
                status: 'complete',
            });
        } catch (error) {
            // 发射错误事件
            this._onLoaderProgressEmitter.fire({
                loaderName: loader.name,
                status: 'error',
                error: error instanceof Error ? error : new Error(String(error)),
            });

            // 重新抛出错误，保持原有错误处理逻辑
            throw error;
        }
    }
}
